// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import ClientOrderService, { ClientOrder } from '../../service/clientOrder.service';

export default function ClientDashboardPage() {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await ClientOrderService.getDashboardMetrics();
        setMetrics(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#A77B58' }} />
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Active Orders',
      value: metrics.totalOrders,
      subtitle: `${metrics.inTransitOrders} Consignments In Transit`,
      icon: <LocalShippingIcon sx={{ fontSize: 28, color: '#A77B58' }} />,
      bg: '#FAF6F0',
      link: '/order-tracker',
      cta: 'Open Order Tracker',
    },
    {
      title: 'Inquiries (Follow-Ups)',
      value: metrics.totalInquiries,
      subtitle: 'Active buyer inquiry threads',
      icon: <QuestionAnswerIcon sx={{ fontSize: 28, color: '#0284c7' }} />,
      bg: '#f0f9ff',
      link: '/follow-ups/inquiries',
      cta: 'View Inquiries',
    },
    {
      title: 'REQ-samples (Follow-Ups)',
      value: metrics.totalSamples,
      subtitle: 'Product sample shipments',
      icon: <Inventory2Icon sx={{ fontSize: 28, color: '#059669' }} />,
      bg: '#ecfdf5',
      link: '/follow-ups/req-samples',
      cta: 'View Samples',
    },
    {
      title: 'Quotations (Follow-Ups)',
      value: metrics.totalQuotations,
      subtitle: 'Verified supplier commercial quotes',
      icon: <RequestQuoteIcon sx={{ fontSize: 28, color: '#7c3aed' }} />,
      bg: '#f5f3ff',
      link: '/follow-ups/quotations',
      cta: 'View Quotations',
    },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Welcome Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 4,
          borderRadius: 3,
          bgcolor: '#FFFFFF',
          border: '1px solid #E8DFD5',
          boxShadow: '0 4px 20px rgba(95, 75, 59, 0.05)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#3B3027' }}>
            Client Post-Login Dashboard
          </Typography>
          <Typography sx={{ color: '#756B63', fontSize: '13.5px', mt: 0.5 }}>
            Manage and track all export consignments, follow-up threads, and supplier connect status updates.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="large"
          component={RouterLink}
          to="/order-tracker"
          startIcon={<LocalShippingIcon />}
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 1.2,
            px: 3,
            bgcolor: '#A77B58',
            color: '#FFFFFF',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(167, 123, 88, 0.3)',
            '&:hover': { bgcolor: '#936948' },
          }}
        >
          Open Order Tracker Table
        </Button>
      </Paper>

      {/* KPI Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statCards.map((card, i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid #E8DFD5',
                bgcolor: '#FFFFFF',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#756B63' }}>
                    {card.title}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: card.bg }}>
                    {card.icon}
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#3B3027' }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#756B63', mt: 0.5 }}>
                  {card.subtitle}
                </Typography>
              </CardContent>
              <Divider sx={{ borderColor: '#F5F0EB' }} />
              <Box sx={{ px: 2.5, py: 1.2, bgcolor: '#FAF8F5' }}>
                <Button
                  component={RouterLink}
                  to={card.link}
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    p: 0,
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#A77B58',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  {card.cta}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Active Supplier Connect Alerts Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          bgcolor: '#FFFFFF',
          border: '1px solid #E8DFD5',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: '#d97706', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Supplier Connect • Active Alert Watch
            </Typography>
          </Box>
          <Chip
            label="Linked to Order Tracker Tags"
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '11px' }}
          />
        </Box>

        <Grid container spacing={2}>
          {metrics.recentAlerts.map((alert: any, idx: number) => (
            <Grid item xs={12} md={6} key={idx}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alert.alertType === 'Normal' ? '#f0fdf4' : '#fffbeb',
                  border: `1px solid ${alert.alertType === 'Normal' ? '#bbf7d0' : '#fde68a'}`,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '13.5px', color: alert.alertType === 'Normal' ? '#166534' : '#92400e' }}>
                    {alert.tagName}
                  </Typography>
                  <Chip
                    label={alert.actionStatus}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      fontWeight: 700,
                      bgcolor: alert.actionStatus === 'Resolved' ? '#dcfce7' : '#fef08a',
                      color: alert.actionStatus === 'Resolved' ? '#15803d' : '#854d0e',
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#5F4B3B', lineHeight: 1.5 }}>
                  {alert.reasonDesc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Recent Orders Preview */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: '#FFFFFF',
          border: '1px solid #E8DFD5',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#3B3027' }}>
              Recent Consignments in Order Tracker
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#756B63' }}>
              Quick preview of recent orders with Supplier Connect tags and tracking status
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/order-tracker"
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: '#A77B58',
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            View All in Order Tracker
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#FAF6F0' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>O.Id</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>O. Detail</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>O. Tag (Alert)</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>O. Status</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#5F4B3B', fontSize: '13px' }}>Vendor Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.recentOrders.map((order: ClientOrder) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: '13px' }}>{order.orderId}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{order.orderDetail.productName}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#756B63' }}>{order.orderDetail.totalAmount}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.orderTag.tagName}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: order.orderTag.alertType === 'Normal' ? '#ecfdf5' : '#fffbeb',
                        color: order.orderTag.alertType === 'Normal' ? '#059669' : '#d97706',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.orderStatus.currentStageLabel}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        bgcolor: '#eff6ff',
                        color: '#1d4ed8',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{order.vendorDetail.vendorName}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#756B63' }}>{order.vendorDetail.city}, {order.vendorDetail.country}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
